import React, { useState, useMemo, useEffect } from "react";
import "./applicantcourses.css";
import htmlandcss from "./html&css 1.jfif";
import python from "./python.jfif";
import interviewprep from "./interviewpreparedness.jpeg";
import sqlimg from "./sql.jfif";
import javascript from "./javascript.jpeg";
import reactimg from "./React-JS.png";
import javaexceptionsimg from "./java_exceptions_algorithms.jpeg";
import java from "./Java.png";
import springboot from "./Springboot.png";
import dataanalysisimg from "./data analysis.jpg";
import nlpimg from "./nlp.jpg";
import deeplearningimg from "./deep learning.jpg";
import statisticsimg from "./Statistics.jpg";
import AdvancedNLP from "./Advanced NLP.jpg";
import MachineLearning from "./Machine Learning.jpg";
import GenerativeAI from "./Generative AI.jpg";
import { useNavigate } from "react-router-dom";
import ProgressAPIService from "./ProgressAPIService";
import { useUserContext } from "../../common/UserProvider";




// Interview prep image lives in /public — use URL, not import
// const interviewprep = process.env.PUBLIC_URL + "/download.png";

const ApplicantCourses = () => {
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [coursesProgress, setCoursesProgress] = useState({});
  const [unlockedCourses, setUnlockedCourses] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useUserContext();
  const applicantId = user?.id;

  const handleCourseAccess = async (course) => {
    const defaultUnlockedIds = [1, 2, 4, 7];
    if (defaultUnlockedIds.includes(course.id)) {
      navigate(`/course/${course.name.toLowerCase()}`);
      return;
    }

    // If we already pre-fetched and know it is locked
    if (unlockedCourses[course.id] === false) {
      alert("🔒 This course is locked. Please contact the admin to unlock this course.");
      return;
    }

    // If we already pre-fetched and know it is unlocked
    if (unlockedCourses[course.id] === true) {
      navigate(`/course/${course.name.toLowerCase()}`);
      return;
    }

    // Fallback: dynamic API check if not pre-fetched yet
    try {
      const unlocked = await ProgressAPIService.checkCourseAccess(applicantId, course.id);

      setUnlockedCourses((prev) => ({ ...prev, [course.id]: unlocked }));

      if (!unlocked) {
        alert("🔒 This course is locked. Please contact the admin to unlock this course.");
        return;
      }

      navigate(`/course/${course.name.toLowerCase()}`);
    } catch (error) {
      console.error("Failed to check course access", error);
      // Fallback: navigate if request fails
      navigate(`/course/${course.name.toLowerCase()}`);
    }
  };

  const coursesData = [
    { id: 10, name: "Data Analysis", img: dataanalysisimg },
    { id: 2, name: "Python", img: python },
    { id: 1, name: "HTML & CSS", img: htmlandcss },
    { id: 3, name: "Java", img: java },
    { id: 4, name: "SQL", img: sqlimg },
    { id: 5, name: "JavaScript & ES6", img: javascript },
    { id: 6, name: "react", img: reactimg },
    { id: 7, name: "Interview Preparedness", img: interviewprep },
    { id: 8, name: "java exceptions and algorithms", img: javaexceptionsimg },
    { id: 9, name: "springboot", img: springboot },
    { id: 11, name: "NLP", img: nlpimg },
    { id: 12, name: "Deep Learning", img: deeplearningimg },
    { id: 13, name: "Statistics", img: statisticsimg },
    { id: 14, name: "Advanced NLP", img: AdvancedNLP },
    { id: 15, name: "Machine Learning", img: MachineLearning },
    { id: 16, name: "Generative AI", img: GenerativeAI }
  ];

  const coursesMap = {
    "data analysis": 10,
    "nlp": 11,
    "deep learning": 12,
    "deeplearning": 12,
    "statistics": 13,
    "html & css": 1,
    "python": 2,
    "java": 3,
    "sql": 4,
    "springboot": 9,
    "javascript & es6": 5,
    "react": 6,
    "java exceptions and algorithms": 8,
    "interview preparedness": 7,
    "advanced nlp": 14,
    "machine learning": 15,
    "generative ai": 16
  };

  // Load progress from backend on component mount
  useEffect(() => {
    const loadCoursesProgress = async () => {
      if (!applicantId) return;

      try {
        setLoading(true);
        // Get all courses progress for this applicant
        const applicantCourses = await ProgressAPIService.getApplicantProgress(applicantId);

        // Convert to object with course names as keys for easy lookup
        const progressMap = {};
        for (const course of applicantCourses) {
          const courseNameLower = course.courseName.toLowerCase();
          if (courseNameLower === "interview preparedness") {
            try {
              const topicsProgress = await ProgressAPIService.getCourseTopics(course.id);
              const totalProgress = topicsProgress.reduce((sum, t) => sum + (t.topicProgress || 0), 0);
              const calculatedProgress = Math.round(totalProgress / 10);
              progressMap[courseNameLower] = calculatedProgress;
            } catch (e) {
              console.error("Failed to load interview prep topics progress:", e);
              progressMap[courseNameLower] = course.overallProgress;
            }
          } else {
            progressMap[courseNameLower] = course.overallProgress;
          }
        }

        setCoursesProgress(progressMap);

        // Fetch unlock/lock status for each course dynamically
        const defaultUnlockedIds = [1, 2, 4, 7];
        const accessMap = {};
        await Promise.all(
          coursesData.map(async (course) => {
            if (defaultUnlockedIds.includes(course.id)) {
              accessMap[course.id] = true;
              return;
            }
            try {
              const unlocked = await ProgressAPIService.checkCourseAccess(applicantId, course.id);
              console.log(`[ApplicantCourses] Course ${course.name} (ID: ${course.id}) => unlocked status: ${unlocked}`);
              accessMap[course.id] = unlocked;
            } catch (error) {
              console.error(`[ApplicantCourses] Error checking access for course ${course.name} (ID: ${course.id}):`, error);
              accessMap[course.id] = false;
            }
          })
        );
        console.log("[ApplicantCourses] Full accessMap loaded:", accessMap);
        setUnlockedCourses(accessMap);
      } catch (error) {
        console.error('Error loading courses progress:', error);
        // Fallback to empty progress
        setCoursesProgress({});
      } finally {
        setLoading(false);
      }
    };

    loadCoursesProgress();
  }, [applicantId, refresh]);

  const getCourseProgress = (courseName) => {
    const key = courseName.toLowerCase();
    return coursesProgress[key] || 0;
  };

  const filteredCourses = useMemo(() => {
    return coursesData.filter((course) =>
      course.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, refresh]);

  return (
    <div className="lms-courses-page">
      <div className="border-style">
        <div className="blur-border-style"></div>

        <div className="dashboard__content">
          <div className="row extraSpace">
            <div className="col-lg-12 col-md-12">

              <div className="main-header-row">
                <h1 className="main-heading">Tech courses</h1>

                <div className="hackathon-search-box">
                  <input
                    type="text"
                    placeholder="Search Courses...."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="hackathon-search-input"
                  />
                </div>
              </div>

              <div className="tv-grid">
                {filteredCourses.map((course) => {
                  const progress = getCourseProgress(course.name);

                  return (
                    <article
                      key={course.id}
                      className="tv-card"
                      onClick={() => handleCourseAccess(course)}
                      style={{
                        position: "relative",
                        cursor: "pointer",
                      }}
                    >
                      {/* Lock overlay badge (not blurred) */}
                      {unlockedCourses[course.id] === false && (
                        <div style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          background: "#fff",
                          color: "#000",
                          padding: "8px 16px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
                          zIndex: 10
                        }}>
                          <span>🔒</span> Locked
                        </div>
                      )}

                      {/* Hover Overlay for Locked Card */}
                      {unlockedCourses[course.id] === false && (
                        <div className="locked-hover-overlay" />
                      )}

                      {/* Card Content wrapper (not blurred anymore) */}
                      <div style={{
                        filter: "none",
                        opacity: 1,
                        transition: "all 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%"
                      }}>
                        <div className="tv-media">
                          <img src={course.img} alt={course.name} />
                        </div>

                        <div className="tv-content">
                          <h4>{course.name}</h4>

                          {/* Progress Bar */}
                          <div style={{
                            height: "6px",
                            background: "#eee",
                            borderRadius: "10px",
                            overflow: "hidden"
                          }}>
                            <div
                              style={{
                                width: `${progress}%`,
                                height: "100%",
                                background: "#e49723ff"
                              }}
                            />
                          </div>

                          <p style={{ fontSize: "12px" }}>
                            {progress}% completed
                          </p>

                          {/* Start / Continue Button */}
                          <div style={{ marginTop: "8px" }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCourseAccess(course);
                              }}
                              style={{
                                fontSize: "13px",
                                background: unlockedCourses[course.id] === false
                                  ? "#ffffff"
                                  : parseInt(progress) > 0
                                    ? "linear-gradient(90deg, #f7c191ff 0%, #f7c191ff 100%)"
                                    : "linear-gradient(90deg, #FBBB5C 0%, #E66A0E 100%)",
                                color: unlockedCourses[course.id] === false ? "#000000" : parseInt(progress) > 0 ? "#b85809ff" : "#fff",
                                cursor: "pointer",
                                border: unlockedCourses[course.id] === false ? "1px solid #e2e8f0" : "none",
                                borderRadius: "4px",
                                padding: "6px 20px",
                                width: "100%",
                                boxShadow: unlockedCourses[course.id] === false
                                  ? "none"
                                  : parseInt(progress) > 0
                                    ? "0px 0px 12px #f7c191ff"
                                    : "0px 0px 15px #F7AA4B",
                                height: "32px",
                                fontWeight: "600",
                              }}
                            >
                              {unlockedCourses[course.id] === false ? "🔒 Locked" : parseInt(progress) > 0 ? "▶ Continue" : "▶ Start course"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicantCourses;
